"use client";
import { useRef, useState } from "react";
import { Banner, Field, FieldGrid, Input } from ".";
import { Choice, CommandForm, Evidence, PersonPicker, type PersonOption } from "./record-tools";
import { StaffReauth } from "./staff-reauth";
import { ApiError } from "../../lib/api";
import {
  ATTRIBUTE_LABELS,
  FACT_KINDS,
  type DictionaryItem,
  type MemberDetail,
  type MemberFact
} from "./member-model";
import type { OrgNode } from "./organization-model";
import { flatten } from "./organization-model";

export type MemberAction =
  "create" | "membership" | "attribute" | "fact" | "binding" | "merge" | "supersede" | "identity";
const TITLES: Record<MemberAction, string> = {
  create: "新建会员档案",
  membership: "登记会籍资料",
  identity: "登记身份证号",
  attribute: "更正申报资料",
  fact: "登记履历",
  binding: "签发核验绑定码",
  merge: "合并重复档案",
  supersede: "收档履历"
};
export function MemberEditor({
  action,
  detail,
  fact,
  organizations,
  dictionaries,
  onCancel,
  onSaved,
  defaultOrganization
}: {
  action: MemberAction;
  detail: MemberDetail | null;
  fact?: MemberFact;
  organizations: OrgNode[];
  dictionaries: Record<string, DictionaryItem[]>;
  onCancel: () => void;
  onSaved: (result: unknown) => void;
  defaultOrganization: string;
}) {
  const [initial] = useState(() => ({
    displayName: "",
    mobile: "",
    organizationId: defaultOrganization,
    sourceRef: "",
    reason: "",
    statusCode: detail?.membership.statusCode ?? "",
    approvedOn: detail?.membership.approvedOn ?? "",
    sectorClassification: detail?.membership.sectorCode ?? "",
    ethnicityCode: detail?.ethnicityCode ?? "",
    politicalStatusCode: detail?.politicalStatusCode ?? "",
    localNumber: detail?.membership.localNumber ?? "",
    centralNumber: detail?.membership.centralNumber ?? "",
    // WO-113：人身字段与上级报表口径。三态布尔用 "" | "true" | "false" 存，提交时转 null / true / false。
    birthDate: detail?.birthDate ?? "",
    workStartOn: detail?.workStartOn ?? "",
    nativePlace: detail?.nativePlace ?? "",
    householdRegistration: detail?.householdRegistration ?? "",
    representativeTier: triState(detail?.membership.representativeTier),
    isCentralEnterprise: triState(detail?.membership.isCentralEnterprise),
    enterpriseScaleCode: detail?.membership.enterpriseScaleCode ?? "",
    postingDepartmentTypeCode: detail?.membership.postingDepartmentTypeCode ?? "",
    postingRankCode: detail?.membership.postingRankCode ?? "",
    idDocumentNumber: "",
    attributeCode: "MOBILE",
    value: "",
    kind: "EDUCATION",
    title: "",
    subtitle: "",
    primaryCode: "",
    secondaryCode: "",
    note: "",
    startOn: "",
    endOn: ""
  }));
  const [form, setForm] = useState(initial),
    [person, setPerson] = useState<PersonOption | null>(null),
    [confirmed, setConfirmed] = useState(false),
    [clearIdDocument, setClearIdDocument] = useState(false);
  // 登记身份证号前先重认证：弹窗开着时把提交挂起，口令通过才放行，取消就当没提交。
  const [reauthOpen, setReauthOpen] = useState(false);
  const reauthWaiter = useRef<{ resolve: () => void; reject: (error: Error) => void } | null>(null);
  const requireReauth = () =>
    new Promise<void>((resolve, reject) => {
      reauthWaiter.current = { resolve, reject };
      setReauthOpen(true);
    });
  const change = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const dict = (code: string) =>
    (dictionaries[code] ?? []).map((d) => ({ value: d.itemCode, label: d.displayName }));
  const input = (
    key: keyof typeof form,
    label: string,
    required = false,
    type = "text",
    maxLength = 200
  ) => (
    <Field label={label}>
      <Input
        type={type}
        required={required}
        value={form[key]}
        maxLength={maxLength}
        minLength={key === "displayName" ? 2 : undefined}
        onChange={(e) => change(key, e.target.value)}
      />
    </Field>
  );
  const select = (key: keyof typeof form, label: string, code: string, required = false) => (
    <Choice
      label={label}
      value={form[key]}
      onChange={(v) => change(key, v)}
      options={dict(code)}
      required={required}
      empty={required ? "请选择" : "未登记"}
    />
  );
  const spec = FACT_KINDS.find((k) => k.kind === form.kind)!;
  const danger = action === "merge" || action === "supersede";
  const base = `/api/v1/member-records/${encodeURIComponent(detail?.personId ?? "")}`;
  const membershipKeys = [
    "statusCode",
    "approvedOn",
    "sectorClassification",
    "ethnicityCode",
    "politicalStatusCode",
    "localNumber",
    "centralNumber",
    "birthDate",
    "workStartOn",
    "nativePlace",
    "householdRegistration",
    "representativeTier",
    "isCentralEnterprise",
    "enterpriseScaleCode",
    "postingDepartmentTypeCode",
    "postingRankCode"
  ] as const;
  const triStateKeys: readonly string[] = ["representativeTier", "isCentralEnterprise"];
  const membershipChanged = membershipKeys.some((key) => initial[key] !== form[key]);
  const idDocumentReady = clearIdDocument || /^\d{17}[\dXx]$/.test(form.idDocumentNumber.replace(/\s/g, ""));
  const triChoice = (key: "representativeTier" | "isCentralEnterprise", label: string) => (
    <Choice
      label={label}
      value={form[key]}
      onChange={(v) => change(key, v)}
      options={[
        { value: "true", label: "是" },
        { value: "false", label: "否" }
      ]}
      empty="未登记"
    />
  );
  return (
    <>
    {reauthOpen && (
      <StaffReauth
        title="登记身份证号"
        description="身份证号属敏感个人信息，登记须本人重认证；库内只存密文，页面默认只显示掩码。"
        onComplete={async () => {
          reauthWaiter.current?.resolve();
          reauthWaiter.current = null;
        }}
        onClose={() => {
          setReauthOpen(false);
          reauthWaiter.current?.reject(new ApiError(0, "REAUTH_CANCELLED"));
          reauthWaiter.current = null;
        }}
      />
    )}
    <CommandForm
      title={TITLES[action]}
      description={
        detail
          ? `${detail.displayName} · ${detail.organizationName}`
          : "根据已有资料建立档案，随后由会员本人完成身份核验。"
      }
      dirty={JSON.stringify(form) !== JSON.stringify(initial) || !!person || confirmed}
      onCancel={onCancel}
      onSaved={onSaved}
      danger={danger}
      label={
        action === "create"
          ? "建立档案"
          : action === "binding"
            ? "确认签发"
            : action === "merge"
              ? "确认合并"
              : action === "supersede"
                ? "确认收档"
                : action === "identity"
                  ? "确认登记"
                  : "保存变更"
      }
      disabled={
        (action === "merge" && (!person || !confirmed)) ||
        ((action === "binding" || action === "supersede") && !confirmed) ||
        (action === "membership" && !membershipChanged) ||
        (action === "identity" && !idDocumentReady)
      }
      onSubmit={async (send) => {
        const evidence = { sourceRef: form.sourceRef.trim(), reason: form.reason.trim() };
        if (action === "create")
          return send("/api/v1/member-records", {
            displayName: form.displayName.trim(),
            mobile: form.mobile.replaceAll(/\s/g, ""),
            organizationId: form.organizationId,
            ...evidence
          });
        if (action === "binding") return send(`${base}/binding-code`, evidence);
        if (action === "supersede")
          return send(`${base}/facts/${encodeURIComponent(fact!.id)}/supersede`, evidence);
        if (action === "merge")
          return send(`${base}/merge`, { intoPersonId: person!.personId, ...evidence });
        if (action === "attribute") {
          // 手机号同时是会员认领的凭据（ADR-0042），改号与登记身份证号同一道重认证闸。
          if (form.attributeCode === "MOBILE") await requireReauth();
          return send(`${base}/attributes`, {
            attributeCode: form.attributeCode,
            value: form.value.trim(),
            ...evidence
          });
        }
        if (action === "membership") {
          const changes: Record<string, unknown> = {};
          for (const key of membershipKeys)
            if (form[key] !== initial[key])
              changes[key] = triStateKeys.includes(key)
                ? form[key] === "" ? null : form[key] === "true"
                : form[key] || null;
          return send(`${base}/membership-facts`, {
            ...changes,
            expectedVersion: detail!.version,
            ...evidence
          });
        }
        if (action === "identity") {
          // 先过重认证闸，再提交；取消重认证就不提交。
          await requireReauth();
          return send(`${base}/identity-document`, {
            idDocumentNumber: clearIdDocument ? null : form.idDocumentNumber.replace(/\s/g, "").toUpperCase(),
            expectedVersion: detail!.version,
            ...evidence
          });
        }
        return send(`${base}/facts`, {
          kind: form.kind,
          title: form.title.trim(),
          ...Object.fromEntries(
            ["subtitle", "primaryCode", "secondaryCode", "note", "startOn", "endOn"]
              .filter((key) => form[key as keyof typeof form])
              .map((key) => [key, form[key as keyof typeof form].trim()])
          ),
          ...evidence
        });
      }}
    >
      <section className="record-section">
        <h2>{action === "create" ? "基本资料" : "办理内容"}</h2>
        {action === "create" && (
          <FieldGrid>
            {input("displayName", "姓名", true, "text", 40)}
            {input("mobile", "核验手机号", true, "tel", 20)}
            <Choice
              label="所属组织"
              required
              value={form.organizationId}
              onChange={(v) => change("organizationId", v)}
              options={flatten(organizations)
                .filter(
                  ({ node }) =>
                    node.lifecycle === "ACTIVE" && node.kind?.canHoldMembership !== false
                )
                .map(({ node }) => ({ value: node.id, label: node.name }))}
            />
          </FieldGrid>
        )}
        {action === "membership" && (
          <>
            <p className="record-muted">只提交本次改动的字段。会籍状态与批准日期须有明确依据。</p>
            <FieldGrid>
              {select("statusCode", "会籍状态", "MEMBERSHIP_STATUS", true)}
              {input("approvedOn", "批准入会日期", !!initial.approvedOn, "date")}
              {select("sectorClassification", "界别", "SECTOR_CLASSIFICATION")}
              {select("ethnicityCode", "民族", "ETHNICITY")}
              {select("politicalStatusCode", "政治面貌", "POLITICAL_STATUS")}
              {input("localNumber", "地方会员编号", false, "text", 64)}
              {input("centralNumber", "中央会员编号", false, "text", 64)}
            </FieldGrid>
            <h3>人身信息</h3>
            <p className="record-muted">按会员登记表登记；年龄与工作年限由系统推算，不另填。身份证号在「登记身份证号」里单独办理。</p>
            <FieldGrid>
              {input("birthDate", "出生日期", false, "date")}
              {input("workStartOn", "参加工作时间", false, "date")}
              {input("nativePlace", "籍贯", false, "text", 120)}
              {input("householdRegistration", "户籍所在地", false, "text", 120)}
            </FieldGrid>
            <h3>上级报表口径</h3>
            <p className="record-muted">只作向上报数的口径标记，不参与任何评价与排序；会员本人看不到这一组。</p>
            <FieldGrid>
              {triChoice("representativeTier", "中上层")}
              {select("enterpriseScaleCode", "企业规模", "ENTERPRISE_SCALE")}
              {triChoice("isCentralEnterprise", "是否央企")}
              {select("postingDepartmentTypeCode", "实职安排部门类型", "POSTING_DEPARTMENT_TYPE")}
              {select("postingRankCode", "实职安排职级", "POSTING_RANK")}
            </FieldGrid>
          </>
        )}
        {action === "identity" && (
          <>
            <Banner tone="warning">
              身份证号属敏感个人信息：提交前须重新验证口令；库内只存密文，档案页默认只显示掩码，查看全号逐次留痕。
            </Banner>
            <p className="record-muted">
              当前：{detail?.idDocument.recorded ? `已登记（${detail.idDocument.masked ?? "掩码不可用"}）` : "未登记"}
            </p>
            <FieldGrid>
              <Field label="身份证号" hint="18 位，末位可为 X；与已登记的出生日期不一致时会被拒绝。">
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={20}
                  disabled={clearIdDocument}
                  value={form.idDocumentNumber}
                  onChange={(e) => change("idDocumentNumber", e.target.value)}
                />
              </Field>
            </FieldGrid>
            {detail?.idDocument.recorded && (
              <label className="record-check">
                <input
                  type="checkbox"
                  checked={clearIdDocument}
                  onChange={(e) => {
                    setClearIdDocument(e.target.checked);
                    if (e.target.checked) change("idDocumentNumber", "");
                  }}
                />
                清除已登记的号码（登记有误时用；原记录在办理记录里留痕）
              </label>
            )}
          </>
        )}
        {action === "attribute" && (
          <>
            <Choice
              label="更正字段"
              required
              value={form.attributeCode}
              onChange={(v) => {
                change("attributeCode", v);
                change("value", "");
              }}
              options={Object.entries(ATTRIBUTE_LABELS)
                .filter(([key]) => key !== "INTERNAL_DUTY")
                .map(([value, label]) => ({ value, label }))}
            />
            <p className="record-muted">
              当前值：{detail?.declaredAttributes[form.attributeCode] || "未申报"}
            </p>
            {input("value", "更正后的内容", true)}
            <p className="record-muted">会内职务由组织届次与任职记录派生。</p>
          </>
        )}
        {action === "fact" && (
          <>
            <Choice
              label="履历类别"
              required
              value={form.kind}
              onChange={(kind) =>
                setForm((prev) => ({
                  ...prev,
                  kind,
                  title: "",
                  subtitle: "",
                  primaryCode: "",
                  secondaryCode: "",
                  note: "",
                  startOn: "",
                  endOn: ""
                }))
              }
              options={FACT_KINDS.map((f) => ({ value: f.kind, label: f.label }))}
            />
            <FieldGrid>
              {input("title", spec.titleLabel, true, "text", 120)}
              {spec.subtitleLabel &&
                input(
                  "subtitle",
                  spec.subtitleLabel.replace("（必填）", ""),
                  spec.kind === "SOCIAL_ROLE"
                )}
              {spec.primary &&
                select(
                  "primaryCode",
                  dictionaries[spec.primary] ? "类别" : "类别（字典未配置）",
                  spec.primary,
                  true
                )}
              {spec.secondary && select("secondaryCode", "细分类型", spec.secondary)}
              {input("startOn", "开始日期", false, "date")}
              {input("endOn", "结束日期", false, "date")}
              {input("note", "补充说明")}
            </FieldGrid>
          </>
        )}
        {action === "binding" && (
          <Banner tone="warning">
            新码签发后，旧码立即失效。明文仅在签发结果中显示，请当面交给本人。
          </Banner>
        )}
        {action === "merge" && (
          <>
            <Banner tone="warning">
              合并后，当前档案退出在用名单，记录继续保留。此操作不可撤回，请先核实两份档案属于同一人。
            </Banner>
            <PersonPicker
              endpoint="/api/v1/member-records"
              value={person}
              onChange={setPerson}
              exclude={detail?.personId}
            />
            {person && (
              <div className="record-merge">
                <span>
                  并入档案
                  <br />
                  <strong>{detail?.displayName}</strong>
                  <small>{detail?.organizationName}</small>
                </span>
                <span>→</span>
                <span>
                  保留档案
                  <br />
                  <strong>{person.displayName}</strong>
                  <small>{person.organizationName}</small>
                </span>
              </div>
            )}
          </>
        )}
        {action === "supersede" && (
          <Banner tone="warning">
            将收档「{fact?.title}」。该条不再计入当前履历，原始记录保留。
          </Banner>
        )}
        {(danger || action === "binding") && (
          <label className="record-check">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            我已核对对象，并知悉上述影响
          </label>
        )}
      </section>
      <Evidence values={form} change={change} />
    </CommandForm>
    </>
  );
}

/** 三态布尔进表单：null → ""，true → "true"，false → "false"。 */
function triState(value: boolean | null | undefined): string {
  return value === null || value === undefined ? "" : value ? "true" : "false";
}
